import { useMemo } from "react";

import { UseLuminanceArgs } from "./useLuminance.decl";

const WHITE = "#ffffff";
const BLACK = "#000000";

/** sRGB channel max, used to normalize 0-255 channels to 0-1. */
const SRGB_CHANNEL_MAX = 255;
/** IEC 61966-2-1 linearization threshold. */
const SRGB_LINEAR_THRESHOLD = 0.03928;
const SRGB_LINEAR_DIVISOR = 12.92;
const SRGB_OFFSET = 0.055;
const SRGB_SCALE = 1.055;
/** IEC 61966-2-1 gamma. */
const SRGB_GAMMA = 2.4;
/** Rec. 709 relative-luminance weights. */
const LUMINANCE_RED_WEIGHT = 0.2126;
const LUMINANCE_GREEN_WEIGHT = 0.7152;
const LUMINANCE_BLUE_WEIGHT = 0.0722;
/** WCAG 2 contrast-ratio offset. */
const CONTRAST_RATIO_OFFSET = 0.05;

// Using this article's code "https://dev.to/alvaromontoro/building-your-own-color-contrast-checker-4j7o"
function hexToRgb(hex: string) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  type ExpandShorthandHexOptions = {
    groups: { r: string; g: string; b: string };
  };

  const expandShorthandHex = (
    _match: string,
    { groups: { r, g, b } }: ExpandShorthandHexOptions
  ) => r + r + g + g + b + b;

  /* Host String.replace still invokes (match, ...groups). */
  const replaced = hex.replace(shorthandRegex, (...hostArgs) => {
    const [match, r, g, b] = hostArgs as [string, string, string, string];
    return expandShorthandHex(match, { groups: { r, g, b } });
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(replaced);

  if (!result) {
    return null;
  }

  const [_, r, g, b] = result;

  // From base 16 to base 10
  return {
    r: parseInt(r, 16),
    g: parseInt(g, 16),
    b: parseInt(b, 16),
  };
}

type LuminanceOptions = {
  g: number;
  b: number;
};

function luminance(r: number, { g, b }: LuminanceOptions) {
  const luminances = [r, g, b].map((v) => {
    const value = v / SRGB_CHANNEL_MAX;
    return value <= SRGB_LINEAR_THRESHOLD
      ? value / SRGB_LINEAR_DIVISOR
      : ((value + SRGB_OFFSET) / SRGB_SCALE) ** SRGB_GAMMA;
  });

  const [redLuminance, greenLuminance, blueLuminance] = luminances;

  return (
    redLuminance * LUMINANCE_RED_WEIGHT +
    greenLuminance * LUMINANCE_GREEN_WEIGHT +
    blueLuminance * LUMINANCE_BLUE_WEIGHT
  );
}

const FALLBACK_RGB_COLOR = { r: 0, g: 179, b: 136 };
const FALLBACK_COLOR = "#00b388";

/**
 * Custom hook to determine text color and background color based on luminance.
 *
 * @param {Object} args - The arguments for the hook.
 * @param {string} args.color - The color to analyze for luminance.
 * @param {number} args.threshold - The threshold for determining text color.
 *
 * @returns {Object} - An object containing textColor and bgColor based on luminance analysis.
 *   - textColor {string} - The determined text color (either WHITE or BLACK).
 *   - bgColor {string} - The background color, using the input color or a fallback if input color is not valid
 */
export function useLuminance({ color, threshold }: UseLuminanceArgs) {
  // Transform the input color to RGB format
  const transformedColor = useMemo(() => hexToRgb(color), [color]);
  const rgbColor = useMemo(
    () => transformedColor ?? FALLBACK_RGB_COLOR,
    [transformedColor]
  );
  const whiteRgb = useMemo(() => hexToRgb(WHITE), []);

  const tagLuminance = useMemo(
    () => luminance(rgbColor.r, { g: rgbColor.g, b: rgbColor.b }),
    [rgbColor]
  );
  const whiteLuminance = useMemo(
    () =>
      luminance(whiteRgb?.r ?? 0, {
        g: whiteRgb?.g ?? 0,
        b: whiteRgb?.b ?? 0,
      }),
    [whiteRgb]
  );

  const ratio = useMemo(
    () =>
      tagLuminance > whiteLuminance
        ? (whiteLuminance + CONTRAST_RATIO_OFFSET) /
          (tagLuminance + CONTRAST_RATIO_OFFSET)
        : (tagLuminance + CONTRAST_RATIO_OFFSET) /
          (whiteLuminance + CONTRAST_RATIO_OFFSET),
    [tagLuminance, whiteLuminance]
  );

  const textColor = ratio < threshold ? WHITE : BLACK;

  return {
    textColor,
    bgColor: transformedColor ? color : FALLBACK_COLOR,
  };
}
